/**
 * Contract editor — two-column page with an explicit-save model.
 *
 *   Top: status bar (draft / pending / …) + Save + Promote-to-pending.
 *   Left: schema-driven form, grouped by section, conditional visibility.
 *   Right: live preview using the same React Component the rendered
 *          contract will use, in mode='final' with current values.
 *
 * Editing is local-only:
 *   - Field edits buffer into `values`; dirty keys are tracked.
 *   - Party adds / removes mutate `localParties`; removed persisted-rows
 *     accumulate in `removedPartyIds`.
 *   - Save flushes everything via `contracts.save_draft` in one txn.
 *   - Promote calls `contracts.promote_to_pending`; if required fields
 *     or party slots are missing the server returns a structured payload
 *     that we surface as a list of remaining items.
 *
 * Property linking still goes through the dedicated link/unlink
 * procedures — it's a write that should be visible immediately and has
 * no diffable shape in the editor's local state.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  DocumentMetaProvider,
  RENDERERS,
  computeSectionNumbers,
  evaluateVisibilityRule,
  formatLeaseDuration,
  effectiveSigningMode,
  getTemplateVersion,
  isFieldVisible,
  readCustomSections,
  resolveObjektNr,
  resolveSectionPath,
  resolveSectionTitle,
  sectionAnchorId,
  templateSupportsCustomSections,
  TemplateDocument,
  templateDocumentParts,
  manualPropertyListKeys,
  PROPERTY_LIST_MANUAL_KEY,
  SignDigitalStatementContext,
  SignIncludePlaceDateContext,
  type AttachmentCreateSeed,
  type AttachmentRef,
  type AutofillSource,
  type TemplateModule,
  type VariableField,
} from '@hyranai/regta-templates';
import {
  ApiError,
  tenantApi,
  type ContractDetail,
  type ContractParty,
  type ContractReview,
} from '../api.js';
import { composePostadress } from '@regta/common/postadress';
import { formatSwedishLegalId, readManualKeys } from '@hyranai/regta-document-ir';
import { partiesUpdatePayload } from '../lib/party-draft.js';
import { useTenantSession } from '../auth-context.js';
import { dataTableXlsxIO } from '../lib/data-table-xlsx.js';
import { useDataTableAutoImport } from '../components/DataTableAutoImport.js';
import { ReviewPanel, ClosedReviewBanner } from '../components/ReviewPanel.js';
import { SigningPanel } from '../components/SigningPanel.js';
import { SigningPlanPanel, useSigningDraft } from '../components/SigningPlanPanel.js';
import { SIGNING_PHASE_STATUSES, signingTabState } from '../lib/signing-tab.js';
import { PropertyAutofillGroup } from '../components/autofill/PropertyAutofillGroup.js';
import { MultiPropertyPanel } from '../components/MultiPropertyPanel.js';
import { PropertyListFields } from '../components/PropertyListFields.js';
import { CustomSectionsEditor } from '../components/CustomSectionsEditor.js';
import { CUSTOM_SECTIONS_KEY } from '@hyranai/regta-templates';
import { BilagorTab } from '../components/BilagorTab.js';
import { CreateBilagaDialog } from '../components/CreateBilagaDialog.js';
import { NotificationsTab } from '../components/NotificationsTab.js';
import { groupReminders } from '../lib/reminder-groups.js';
import {
  locateAnchor,
  scrollToLocateAnchor,
  type LocateKind,
} from '../lib/locate-anchor.js';
import {
  PartiesPanel,
  PartySlotCard,
  type LocalParty,
} from '../components/PartiesPanel.js';
import {
  FrozenPdfPreview,
  StatusBar,
  type SaveState,
} from '../components/ContractStatusBar.js';
import { InlineSlotUpload } from '../components/InlineSlotUpload.js';
import {
  ConnectedContractsBar,
  ConnectedContractsTab,
  useCreatableConnectedTemplates,
} from '../components/ConnectedContractsPanel.js';
import { CopyFromContractModal } from '../components/CopyFromContractModal.js';
import { UnsavedChangesGuard } from '../components/UnsavedChangesGuard.js';
import { ScaledPreview } from '../components/PreviewZoom.js';
import { EDITOR_AVTAL_WIDTH, EDITOR_CHROME_WIDTH } from '../lib/editor-layout.js';
import { Copy, Eye, FileText } from 'lucide-react';
import { useInvalidate, useTenantQuery } from '../query.js';
import { ImportedContractViewPage } from './ImportedContractViewPage.js';
import { usePageTitle } from '../use-page-title.js';
import { useApprovalResetGuard } from '../lib/approval-reset.js';

function fromServerParty(p: ContractParty): LocalParty {
  return {
    _clientId: p.id,
    _persistedId: p.id,
    role: p.role,
    ordinal: p.ordinal,
    id_type: p.idType,
    id_number: p.idNumber,
    legal_name: p.legalName,
    display_name: p.displayName,
    email: p.email,
    phone: p.phone,
    address_line1: p.addressLine1,
    address_line2: p.addressLine2,
    postal_code: p.postalCode,
    city: p.city,
    country: p.country,
    provenance: p.provenance,
    privacy_class: p.privacyClass,
  };
}

/**
 * Mirror a party's snapshot fields into `values` for every autofill
 * directive on the template that matches `(role, ordinal)`. `null`
 * resolver clears every matching key (used when removing a party).
 *
 * Mirrors the server-side `mirrorPartyToValues` in regta-backend so
 * the local preview stays in sync with what save_draft will write.
 */
function mirrorPartyLocally(
  tmpl: TemplateModule | null,
  values: Record<string, unknown>,
  match: { role: LocalParty['role']; ordinal: number },
  resolve: (af: Extract<AutofillSource, { kind: 'party' }>) => unknown,
): Record<string, unknown> {
  if (!tmpl) return values;
  const next = { ...values };
  const manual = readManualKeys(values);
  for (const field of tmpl.variableSchema) {
    const af = field.autofill;
    if (!af || af.kind !== 'party') continue;
    if (af.role !== match.role || af.ordinal !== match.ordinal) continue;
    if (manual.has(field.key)) continue;
    next[field.key] = resolve(af);
  }
  return next;
}

function partyFieldLocal(
  party: LocalParty,
  field: Extract<AutofillSource, { kind: 'party' }>['field'],
): unknown {
  switch (field) {
    case 'legal_name':
      return party.legal_name;
    case 'id_number':
      return party.id_number ? formatSwedishLegalId(party.id_number) : party.id_number;
    case 'email':
      return party.email;
    case 'phone':
      return party.phone;
    case 'address_line1':
      return party.address_line1;
    case 'address_line2':
      return party.address_line2;
    case 'postal_code':
      return party.postal_code;
    case 'city':
      return party.city;
    case 'country':
      return party.country;
    case 'postal_address': {
      const postal = party.postal_code?.trim() || '';
      const city = party.city?.trim() || '';
      const joined = [postal, city].filter(Boolean).join(' ').trim();
      return joined || null;
    }
    case 'composed_address':
      return composePostadress({
        street: party.address_line1,
        line2: party.address_line2,
        postalCode: party.postal_code,
        ort: party.city,
      });
  }
}

function AutoNumberToggle({
  on,
  pending,
  disabled,
  onToggle,
}: {
  on: boolean;
  pending: boolean;
  disabled: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      onClick={onToggle}
      disabled={disabled || pending}
      className="flex items-center gap-2 self-start text-[11px] text-stone-600 disabled:opacity-50"
    >
      <span
        className={`relative inline-flex h-4 w-7 items-center rounded-full transition-colors ${
          on ? 'bg-stone-900' : 'bg-stone-300'
        }`}
      >
        <span
          className={`inline-block h-3 w-3 transform rounded-full bg-white shadow transition-transform ${
            on ? 'translate-x-3.5' : 'translate-x-0.5'
          }`}
        />
      </span>
      <span>{pending ? 'Reserverar …' : 'Auto-generera'}</span>
    </button>
  );
}


export function ContractEditorPage() {
  const { id } = useParams<{ id: string }>();
  const { token, actor } = useTenantSession();
  const invalidate = useInvalidate();
  // Custom-Excel auto-import (hyreslista Phase 3) — `run` is passed to
  // data_table fields; `element` (file input + review modal) is rendered once.
  const { run: dataTableAutoImport, element: dataTableAutoImportEl } =
    useDataTableAutoImport();

  const contractQ = useTenantQuery(
    'contracts.get',
    (cid) => tenantApi.contracts.get(cid as string),
    id ? [id] : [],
    { ignoreActiveGroup: true, revalidateOnFocus: false },
  );
  const partiesQ = useTenantQuery(
    'contract_parties.list',
    (cid) =>
      tenantApi.contract_parties.list({ contract_id: cid as string }),
    id ? [id] : [],
    { ignoreActiveGroup: true, revalidateOnFocus: false },
  );
  // Reviews drive both the panel and the editor's disabled state —
  // when status='in_review' the assigned reviewer keeps full edit
  // access, everyone else (incl. the requester) is read-only until the
  // review closes.
  const reviewsQ = useTenantQuery(
    'reviews.list_for_contract',
    (cid) =>
      tenantApi.reviews.list_for_contract({ contract_id: cid as string }),
    id ? [id] : [],
    { ignoreActiveGroup: true, revalidateOnFocus: false },
  );
  // Drives the "Påminnelser (n)" tab badge. Same endpoint/key as the
  // NotificationsTab query, so SWR dedupes to a single fetch.
  const notificationsQ = useTenantQuery(
    'notifications.list_by_contract',
    (cid) =>
      tenantApi.notifications.list_by_contract({ contract_id: cid as string }),
    id ? [id] : [],
    { revalidateOnFocus: false },
  );
  // Grouped count (REGTA-51): the badge must match the number of
  // reminder cards the tab renders, not the raw row count.
  const reminderCount = groupReminders(
    notificationsQ.data?.notifications ?? [],
  ).filter((g) => !g.allHandled).length;
  const reviewsList: ContractReview[] = reviewsQ.data?.reviews ?? [];
  const activeReview = reviewsList.find((r) => r.status === 'pending') ?? null;
  const isActiveReviewer =
    !!activeReview && !!actor && activeReview.reviewer.id === actor.id;
  // Most recent closed review — reviewsList is server-sorted by
  // requestedAt DESC, so the first non-pending row is the latest.
  const lastClosedReview =
    reviewsList.find((r) => r.status !== 'pending') ?? null;
  // A finished review (approved / rejected / cancelled) exists. Once a
  // review is done it moves out of the always-on header workflow row and
  // into its own "Granskning" tab.
  const hasReviewHistory = reviewsList.some((r) => r.status !== 'pending');

  const contract: ContractDetail | null = contractQ.data?.contract ?? null;
  // ADR-0102 decision 3: confirm once before a change resets a current approval.
  const approvalGuard = useApprovalResetGuard(contract?.approval);
  const serverParties: ContractParty[] = partiesQ.data?.parties ?? [];

  // Connected templates this (root) contract can spawn — drives the
  // "Kopplade avtal" tab, which carries the create affordance once the
  // contract leaves draft (the inline bar hides its create then).
  const creatableConnected = useCreatableConnectedTemplates(
    contract?.templateKey ?? '',
    !!contract && contract.parentContractId === null,
  );

  const [values, setValues] = useState<Record<string, unknown>>({});
  const [dirtyKeys, setDirtyKeys] = useState<Set<string>>(new Set());
  const [localParties, setLocalParties] = useState<LocalParty[]>([]);
  const [removedPartyIds, setRemovedPartyIds] = useState<string[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [missing, setMissing] = useState<{
    fields: string[];
    slots: string[];
    attachmentSlots: string[];
  } | null>(null);
  // Tab strip — 'avtal' is the main contract editor; 'bilagor' is
  // attachments (ADR-0028); 'paminnelser' is the R5/ADR-0031 lifecycle
  // notifications tab. Imports (ImportedContractView)
  // do their own thing and don't see this strip.
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const initialTab = searchParams.get('tab');
  // Below the side-by-side breakpoint, the avtal tab toggles between the
  // form (aside) and the live preview (main). On lg+ both stay visible
  // and this state is ignored.
  const [mobilePreviewOpen, setMobilePreviewOpen] = useState(false);
  const [tab, setTab] = useState<
    'signering' | 'avtal' | 'bilagor' | 'paminnelser' | 'granskning' | 'kopplade'
  >(
    initialTab === 'signering' ||
      initialTab === 'avtal' ||
      initialTab === 'paminnelser' ||
      initialTab === 'bilagor' ||
      initialTab === 'granskning' ||
      initialTab === 'kopplade'
      ? initialTab
      : 'avtal',
  );
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [reservingNumber, setReservingNumber] = useState(false);
  // REGTA-49 — pending "show me this bilaga slot" request from the
  // review-readiness summary; BilagorTab activates the slot's inner tab.
  const [bilagaLocate, setBilagaLocate] = useState<{
    slotId: string;
    seq: number;
  } | null>(null);
  // Drop a handled locate request once the user leaves the Bilagor tab so
  // it can't re-activate a slot when the tab remounts later. Must sit with
  // the other hooks, above the loading early-returns — a hook below them
  // registers late and trips React's hooks-order invariant.
  useEffect(() => {
    if (tab !== 'bilagor') setBilagaLocate(null);
  }, [tab]);
  // Set when the user presses "Fortsätt till signering" in the header on
  // a pre-sign contract — reveals the Signering tab + its options. Local
  // + per session; reset on contract change so the choice re-presents.
  const [proceeding, setProceeding] = useState(false);

  // Inline bilaga creation from an `attachment_ref` picker (ADR-0028
  // addendum 2026-05-27b). The picker awaits a created attachment id; we
  // open the dialog, then resolve the promise once a bilaga is created
  // (or null on cancel) so the picker can select it.
  const [createSeed, setCreateSeed] = useState<AttachmentCreateSeed | null>(null);
  const createResolverRef = useRef<((id: string | null) => void) | null>(null);
  const openCreateAttachment = useCallback(
    (seed: AttachmentCreateSeed): Promise<string | null> =>
      new Promise<string | null>((resolve) => {
        createResolverRef.current = resolve;
        setCreateSeed(seed);
      }),
    [],
  );
  const finishCreateAttachment = useCallback((id: string | null) => {
    setCreateSeed(null);
    const resolve = createResolverRef.current;
    createResolverRef.current = null;
    resolve?.(id);
  }, []);

  // ADR-0100: the signing plan feeds the live preview (planned names, Ort/Datum, statement).
  const signingPlan = useSigningDraft(contract?.id ?? null).data;

  const tmpl: TemplateModule | null = useMemo(() => {
    if (!contract) return null;
    return getTemplateVersion(contract.templateKey, contract.templateVersion);
  }, [contract]);

  // Hydrate when contract identity changes — never on background refetches
  // (which would clobber edits in flight).
  useEffect(() => {
    if (!contract) return;
    setValues(contract.values ?? {});
    setDirtyKeys(new Set());
    setMissing(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contract?.id]);

  // Re-adopt server values when they change underneath us while the editor
  // is pristine. Copy-from-existing mutates the *same* contract server-side
  // and invalidates contracts.get, so contract?.id never changes and the
  // identity effect above won't re-fire — without this the pre-filled values
  // only appeared after a manual page reload (T-11). The pristine guard keeps
  // ordinary background refetches from clobbering edits in flight.
  const valuesSignature = useMemo(
    () => JSON.stringify(contract?.values ?? {}),
    [contract?.values],
  );
  useEffect(() => {
    if (!contract || dirtyKeys.size > 0) return;
    setValues(contract.values ?? {});
    setMissing(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valuesSignature]);

  // Automatic property-list fields are regenerated server-side when the
  // linked properties change. Adopt them (and the manual-keys list) even
  // while other edits are pending, so the panel never shows stale text.
  useEffect(() => {
    if (!contract || !tmpl || dirtyKeys.size === 0) return;
    const keys = (tmpl.variableSchema as ReadonlyArray<VariableField<Record<string, unknown>>>)
      .filter((f) => f.autofill?.kind === 'property_list')
      .map((f) => f.key);
    if (keys.length === 0) return;
    const server = contract.values ?? {};
    const manual = manualPropertyListKeys(server);
    setValues((prev) => {
      const next: Record<string, unknown> = {
        ...prev,
        [PROPERTY_LIST_MANUAL_KEY]: server[PROPERTY_LIST_MANUAL_KEY],
      };
      for (const k of keys) {
        if (!manual.has(k) && !dirtyKeys.has(k)) next[k] = server[k];
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valuesSignature]);

  // Re-seed parties whenever the server-side id set changes — covers
  // contract identity changes AND post-save refetches where the set of
  // ids shifted but the length didn't (koppla loss + re-add lands one
  // new id and drops one old id → length 1 → 1). Without this the local
  // cache stayed dirty forever and a second Save click duplicated the
  // just-inserted party. Signature is stable across same-set refetches
  // so user-driven local edits between saves aren't clobbered.
  const serverPartiesSignature = useMemo(
    () => serverParties.map((p) => p.id).sort().join(','),
    [serverParties],
  );
  useEffect(() => {
    if (!contract) return;
    setLocalParties(serverParties.map(fromServerParty));
    setRemovedPartyIds([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contract?.id, serverPartiesSignature]);

  useEffect(() => {
    const e = contractQ.error ?? partiesQ.error;
    if (e) setErr((e as Error).message);
  }, [contractQ.error, partiesQ.error]);

  // Default tab on navigation: a contract already in the signing phase
  // opens on the Signering tab; everything else opens on Avtal (pre-sign
  // contracts only surface Signering after an explicit proceed, which is
  // reset here per contract). An explicit `?tab=` in the URL always wins.
  // Keyed on contract identity so it never undoes a manual tab switch.
  useEffect(() => {
    if (!contract) return;
    setProceeding(false);
    if (initialTab) return;
    setTab(SIGNING_PHASE_STATUSES.has(contract.status) ? 'signering' : 'avtal');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contract?.id]);

  const dirty =
    dirtyKeys.size > 0 ||
    removedPartyIds.length > 0 ||
    localParties.some((p) => !p._persistedId || p._dirty);

  const onFieldChange = (key: string, next: unknown) => {
    if (!approvalGuard()) return;
    const nextValues: Record<string, unknown> = { ...values, [key]: next };
    // Seed defaults for fields that just became visible due to this
    // change (e.g. negotiation_clause defaults to checked the moment
    // rent_model flips to "Kollektiva hyresförhandlingar").
    const autoSeeded: string[] = [];
    if (tmpl) {
      const schema = tmpl.variableSchema as ReadonlyArray<
        VariableField<Record<string, unknown>>
      >;
      for (const f of schema) {
        if (f.default === undefined) continue;
        const cur = nextValues[f.key];
        if (cur !== undefined && cur !== '') continue;
        const wasVisible = isFieldVisible(f, values);
        const isVisible = isFieldVisible(f, nextValues);
        if (!wasVisible && isVisible) {
          nextValues[f.key] = f.default;
          autoSeeded.push(f.key);
        }
      }
    }
    setValues(nextValues);
    setDirtyKeys((s) => {
      const n = new Set(s);
      n.add(key);
      for (const k of autoSeeded) n.add(k);
      return n;
    });
    setMissing(null);
  };

  // "Auto-generera" switch beside contract_number. ON reserves a fresh
  // 6-digit tenant-scoped number via contracts.reserve_number; OFF
  // re-enables manual entry. Reserved numbers are never recycled —
  // toggling off and on again burns the prior number and issues a new
  // one. The auto flag lives under `__contract_number_auto` in values
  // so the lock state survives reloads. update_draft does a shallow
  // JSONB merge so the meta key persists alongside the user's field.
  const toggleAutoContractNumber = useCallback(async () => {
    if (!token) return;
    const isOn = values.__contract_number_auto === true;
    if (isOn) {
      setValues((prev) => ({ ...prev, __contract_number_auto: false }));
      setDirtyKeys((s) => {
        const n = new Set(s);
        n.add('__contract_number_auto');
        return n;
      });
      setMissing(null);
      return;
    }
    setReservingNumber(true);
    setErr(null);
    try {
      const r = await tenantApi.contracts.reserve_number();
      setValues((prev) => ({
        ...prev,
        contract_number: r.number,
        __contract_number_auto: true,
      }));
      setDirtyKeys((s) => {
        const n = new Set(s);
        n.add('contract_number');
        n.add('__contract_number_auto');
        return n;
      });
      setMissing(null);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setReservingNumber(false);
    }
  }, [token, values.__contract_number_auto]);

  const onAddParty = useCallback(
    (party: LocalParty) => {
      setLocalParties((prev) => {
        // One slot can only hold one party. If the user re-adds, the
        // previous local-only row is dropped; a persisted row is queued
        // for removal so save_draft tears it down before the new insert.
        const occupant = prev.find(
          (p) => p.role === party.role && p.ordinal === party.ordinal,
        );
        let next = prev;
        if (occupant) {
          if (occupant._persistedId) {
            setRemovedPartyIds((ids) =>
              ids.includes(occupant._persistedId!) ? ids : [...ids, occupant._persistedId!],
            );
          }
          next = prev.filter((p) => p._clientId !== occupant._clientId);
        }
        return [...next, party];
      });
      setValues((prev) =>
        mirrorPartyLocally(
          tmpl,
          prev,
          { role: party.role, ordinal: party.ordinal },
          (af) => partyFieldLocal(party, af.field),
        ),
      );
      setMissing(null);
    },
    [tmpl],
  );

  const onRemoveParty = useCallback(
    (clientId: string) => {
      setLocalParties((prev) => {
        const target = prev.find((p) => p._clientId === clientId);
        if (!target) return prev;
        if (target._persistedId) {
          setRemovedPartyIds((ids) =>
            ids.includes(target._persistedId!) ? ids : [...ids, target._persistedId!],
          );
        }
        setValues((vs) =>
          mirrorPartyLocally(
            tmpl,
            vs,
            { role: target.role, ordinal: target.ordinal },
            () => null,
          ),
        );
        return prev.filter((p) => p._clientId !== clientId);
      });
      setMissing(null);
    },
    [tmpl],
  );

  // "Redigera" on a seated party: swap in the edited snapshot and re-fill
  // its auto-mode fields (manual-mode fields keep their text).
  const onUpdateParty = useCallback(
    (clientId: string, next: LocalParty) => {
      setLocalParties((prev) => prev.map((p) => (p._clientId === clientId ? next : p)));
      setValues((prev) =>
        mirrorPartyLocally(
          tmpl,
          prev,
          { role: next.role, ordinal: next.ordinal },
          (af) => partyFieldLocal(next, af.field),
        ),
      );
      setMissing(null);
    },
    [tmpl],
  );

  const save = useCallback(async () => {
    if (!token || !contract || !approvalGuard()) return;
    setSaveState('saving');
    setErr(null);
    try {
      const valuesPayload: Record<string, unknown> = {};
      for (const k of dirtyKeys) valuesPayload[k] = values[k];

      const parties_add = localParties
        .filter((p) => !p._persistedId)
        .map((p) => ({
          role: p.role,
          ordinal: p.ordinal,
          id_type: p.id_type,
          id_number: p.id_number,
          legal_name: p.legal_name,
          display_name: p.display_name,
          email: p.email,
          phone: p.phone,
          address_line1: p.address_line1,
          address_line2: p.address_line2,
          postal_code: p.postal_code,
          city: p.city,
          country: p.country,
          provenance: p.provenance,
        }));

      const parties_update = partiesUpdatePayload(localParties);
      await tenantApi.contracts.save_draft({
        id: contract.id,
        values: Object.keys(valuesPayload).length > 0 ? valuesPayload : undefined,
        parties_add: parties_add.length > 0 ? parties_add : undefined,
        parties_update: parties_update.length > 0 ? parties_update : undefined,
        parties_remove: removedPartyIds.length > 0 ? removedPartyIds : undefined,
      });
      setDirtyKeys(new Set());
      setRemovedPartyIds([]);
      if (parties_update.length > 0) {
        setLocalParties((prev) => prev.map((p) => (p._dirty ? { ...p, _dirty: false } : p)));
      }
      await Promise.all([
        invalidate('contracts.get', contract.id),
        invalidate('contract_parties.list', contract.id),
      ]);
      setSaveState('idle');
    } catch (e) {
      setErr((e as Error).message);
      setSaveState('error');
    }
  }, [token, contract, dirtyKeys, values, localParties, removedPartyIds, invalidate, approvalGuard]);

  const promote = useCallback(async () => {
    if (!token || !contract) return;
    setErr(null);
    try {
      const r = await tenantApi.contracts.promote_to_pending({ id: contract.id });
      if (r.ok) {
        setMissing(null);
        await invalidate('contracts.get', contract.id);
      } else {
        setMissing({
          fields: r.missing_fields,
          slots: r.missing_slots,
          attachmentSlots: r.missing_attachment_slots,
        });
      }
    } catch (e) {
      setErr((e as Error).message);
    }
  }, [token, contract, invalidate]);

  const revertToDraft = useCallback(async () => {
    if (!token || !contract) return;
    // A cancelled signing attempt reopens via the signing seam
    // (supersedes the issued document, clears the attempt).
    const leavingSigning = contract.status === 'cancelled';
    if (
      leavingSigning &&
      !confirm(
        'Återgå till utkast? Avtalet blir redigerbart igen och ett nytt dokument skapas vid nästa utskick. Ändras avtalet bör det granskas på nytt innan det skickas.',
      )
    ) {
      return;
    }
    setErr(null);
    try {
      if (leavingSigning) await tenantApi.signing.return_to_draft({ contract_id: contract.id });
      else await tenantApi.contracts.revert_to_draft({ id: contract.id });
      await invalidate('contracts.get', contract.id);
    } catch (e) {
      setErr((e as Error).message);
    }
  }, [token, contract, invalidate]);

  const archive = useCallback(async () => {
    if (!token || !contract) return;
    if (!confirm(`Arkivera "${contract.title}"?`)) return;
    setErr(null);
    try {
      await tenantApi.contracts.archive({ id: contract.id });
      await invalidate('contracts.list');
      navigate('/contracts');
    } catch (e) {
      setErr((e as Error).message);
    }
  }, [token, contract, invalidate, navigate]);

  const cancelActiveReview = useCallback(async () => {
    if (!token || !contract || !activeReview) return;
    setErr(null);
    try {
      await tenantApi.reviews.cancel({ review_id: activeReview.id });
      await Promise.all([
        invalidate('reviews.list_for_contract', contract.id),
        invalidate('contracts.get', contract.id),
        invalidate('contracts.list'),
        invalidate('reviews.list_my_pending'),
      ]);
    } catch (e) {
      setErr((e as Error).message);
    }
  }, [token, contract, activeReview, invalidate]);

  const requestReview = useCallback(
    async (reviewerId: string, comment: string | undefined) => {
      if (!token || !contract) return;
      setErr(null);
      try {
        await tenantApi.reviews.request({
          contract_id: contract.id,
          reviewer_user_id: reviewerId,
          comment,
        });
        await Promise.all([
          invalidate('reviews.list_for_contract', contract.id),
          invalidate('contracts.get', contract.id),
          invalidate('contracts.list'),
        ]);
      } catch (e) {
        setErr((e as Error).message);
        throw e;
      }
    },
    [token, contract, invalidate],
  );

  // After link/unlink the server-side `contract.values` has new
  // property-autofilled fields. The contract-id-keyed re-seed effect
  // above intentionally skips background refetches, so we have to
  // merge the server's new values into local state here — keeping any
  // currently-dirty keys (user edits in flight) on top.
  const mergeServerValues = useCallback(
    (serverValues: Record<string, unknown>) => {
      setValues((prev) => {
        const next: Record<string, unknown> = { ...serverValues };
        for (const key of dirtyKeys) {
          next[key] = prev[key];
        }
        return next;
      });
    },
    [dirtyKeys],
  );
  const linkProperty = useCallback(
    async (propertyId: string) => {
      if (!token || !contract) return;
      const r = await tenantApi.contracts.link_property({
        id: contract.id,
        property_id: propertyId,
      });
      mergeServerValues(r.values);
      await invalidate('contracts.get', contract.id);
    },
    [token, contract, invalidate, mergeServerValues],
  );
  const unlinkProperty = useCallback(async () => {
    if (!token || !contract) return;
    const r = await tenantApi.contracts.unlink_property({ id: contract.id });
    mergeServerValues(r.values);
    await invalidate('contracts.get', contract.id);
  }, [token, contract, invalidate, mergeServerValues]);

  // Editor is editable on draft, or for the assigned reviewer on in_review.
  // Duplicated here (also computed below as `disabled`) so unsaved-state
  // effects can run before the conditional renders that follow.
  const canEdit =
    !!contract &&
    (contract.status === 'draft' ||
      (contract.status === 'in_review' && isActiveReviewer));

  // Tab title prefix `● ` when there are unsaved changes — the same
  // convention browsers/editors use everywhere (VS Code, Figma, Docs).
  const baseTitle = contract ? contract.title || 'Avtal' : null;
  usePageTitle(
    baseTitle === null ? null : dirty && canEdit ? `● ${baseTitle}` : baseTitle,
  );

  // ⌘S / Ctrl+S to save. Capture-phase so it wins over any field-level
  // handler; preventDefault stops the browser's "save page" dialog.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const isSaveCombo =
        (e.metaKey || e.ctrlKey) && (e.key === 's' || e.key === 'S');
      if (!isSaveCombo) return;
      e.preventDefault();
      if (dirty && canEdit && saveState !== 'saving') void save();
    };
    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  }, [dirty, canEdit, saveState, save]);

  // A 404 on the contract fetch means it was deleted (possibly from
  // another tab or session). SWR keeps stale data on error, so without
  // this gate browser-Back would render the deleted contract as alive.
  if (contractQ.error instanceof ApiError && contractQ.error.status === 404) {
    return (
      <div className="rounded border border-stone-200 bg-white p-4 text-sm text-stone-600">
        Avtalet finns inte längre.{' '}
        <Link to="/contracts" className="font-medium text-stone-900 underline">
          Till avtalslistan
        </Link>
      </div>
    );
  }
  // Beta-lane (document-IR) contracts open in their own editor (ADR-0099).
  if (contract?.documentEngine === 'ir') {
    const qs = searchParams.toString();
    return <Navigate to={`/contracts/${contract.id}/beta${qs ? `?${qs}` : ''}`} replace />;
  }
  if (err && !contract) {
    return (
      <div className="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-700">
        {err}
      </div>
    );
  }
  if (!contract || !tmpl || !token) {
    return <div className="text-sm text-stone-400">loading…</div>;
  }

  // R2 imports get their own view — different chrome (no "not in draft"
  // banner), filtered field set, and the original document on the right
  // instead of a JS template render. See ImportedContractViewPage.
  if (contract.originalDocument) {
    return (
      <div className={EDITOR_CHROME_WIDTH}>
        <ImportedContractViewPage
          contract={contract}
          tmpl={tmpl}
          parties={localParties}
        />
      </div>
    );
  }

  // Editable when the contract is in draft, OR when the actor is the
  // assigned reviewer on an open review (status='in_review'). Other
  // statuses lock the editor.
  const disabled =
    contract.status === 'draft'
      ? false
      : contract.status === 'in_review' && isActiveReviewer
        ? false
        : true;
  const allFields = tmpl.variableSchema as ReadonlyArray<VariableField<Record<string, unknown>>>;
  const propertyFields = allFields.filter((f) => f.autofill?.kind === 'property');
  const regularFields = allFields.filter((f) => !f.autofill);
  // Per ADR-0025: templates declare property cardinality via
  // metadata.propertySlots. Single-property templates (no slot or
  // `multi: false`) keep the existing PropertyAutofillGroup; multi
  // templates render MultiPropertyPanel. The two are mutually
  // exclusive — `kind: 'property'` and `kind: 'property_list'`
  // autofills don't coexist on a single template.
  const propertySlot = tmpl.metadata.propertySlots?.[0] ?? null;
  const isMultiProperty = propertySlot?.multi === true;

  // Section-ref model (per ADR-0049). When the template declares
  // `metadata.sections`, `field.section` is a ref into that list, the §
  // numbers shown in panel legends are computed from visibility (so they
  // match the live preview exactly), and `metadata.inputSections` may
  // merge several document sections into one editor panel. Legacy
  // templates (no `metadata.sections`) keep one panel per `field.section`
  // string with no numbers — `resolveSectionTitle` falls back to the raw
  // string and `numbering` is null.
  const declaredSections = tmpl.metadata.sections;
  const numbering = declaredSections
    ? computeSectionNumbers(declaredSections, values, 'final')
    : null;

  // Per-contract custom sections (ADR-0060). Offered on every
  // single-document section-model template; the array lives under a
  // reserved values key and patches through the normal field path. A custom
  // section is placed after a declared section (`afterRef`); the editor only
  // offers currently-visible sections and seeds new sections before the
  // closing/signing block (second-to-last visible section).
  // Effective signing mode (per ADR-0061) — for `signingOptional` templates
  // it resolves from `values.include_signing`, else the static signingMode.
  const effectiveSigning = effectiveSigningMode(tmpl.metadata, values);
  const supportsCustomSections = templateSupportsCustomSections(tmpl);
  const customSections = supportsCustomSections
    ? readCustomSections(values, 'blank')
    : [];
  const visibleDeclared = supportsCustomSections
    ? (declaredSections ?? []).filter(
        (s) => typeof numbering?.get(s.ref) === 'number',
      )
    : [];
  const customPlacementOptions = visibleDeclared.map((s) => ({
    ref: s.ref,
    label: `§${numbering!.get(s.ref)} ${s.title}`,
  }));
  const customDefaultAfterRef =
    visibleDeclared.length >= 2
      ? visibleDeclared[visibleDeclared.length - 2]!.ref
      : visibleDeclared[visibleDeclared.length - 1]?.ref;

  // 1. Group regular (non-autofill) fields by their top-level section ref,
  //    first appearance order. A field's `section` may be a dotted path
  //    targeting a sub-clause (`'skatter-avgifter.moms.tenant'`); the
  //    grouping key is just the first segment so the panel still aligns
  //    with one document section.
  const topSectionRef = (section: string): string => section.split('.')[0] ?? section;
  type FieldGroup = { ref: string; fields: VariableField<Record<string, unknown>>[] };
  const groups: FieldGroup[] = [];
  for (const f of regularFields) {
    const ref = topSectionRef(f.section);
    let g = groups.find((x) => x.ref === ref);
    if (!g) {
      g = { ref, fields: [] };
      groups.push(g);
    }
    g.fields.push(f);
  }

  // 2. Merge groups into panels via metadata.inputSections. A panel emits
  //    at the position of its first member group; refs not covered by any
  //    inputSection (and editor-only sections like Avtalsuppgifter /
  //    Borgensman, which have no document section) stay standalone.
  type EditorPanel = {
    id: string;
    label: string;
    refs: string[];
    fields: VariableField<Record<string, unknown>>[];
  };
  const inputSections = tmpl.metadata.inputSections ?? [];
  const inputSectionForRef = (ref: string) =>
    inputSections.find((is) => is.sectionRefs.includes(ref)) ?? null;

  const panels: EditorPanel[] = [];
  const consumedInputIds = new Set<string>();
  for (const g of groups) {
    const is = inputSectionForRef(g.ref);
    if (is) {
      if (consumedInputIds.has(is.id)) continue;
      consumedInputIds.add(is.id);
      const refs = is.sectionRefs.filter((r) => groups.some((x) => x.ref === r));
      panels.push({
        id: is.id,
        label: is.label,
        refs,
        fields: refs.flatMap((r) => groups.find((x) => x.ref === r)?.fields ?? []),
      });
    } else {
      panels.push({
        id: g.ref,
        label: resolveSectionTitle(declaredSections, g.ref),
        refs: [g.ref],
        fields: g.fields,
      });
    }
  }

  // Split panels by whether all their fields appear before the first
  // autofill-driven field. Pre-autofill panels (typically Avtalsuppgifter)
  // render above the property + parties panels; the rest below.
  const firstAutofillIdx = allFields.findIndex((f) => !!f.autofill);
  const isPrePanel = (p: EditorPanel): boolean =>
    firstAutofillIdx !== -1 && p.fields.every((f) => allFields.indexOf(f) < firstAutofillIdx);
  const preSections = panels.filter(isPrePanel);
  const postSections = panels.filter((p) => !isPrePanel(p));

  // "§N" / "§A–B" prefix for a panel, from the computed numbering.
  // Editor-only / hidden sections contribute no number.
  const sectionNumberPrefix = (refs: string[]): string => {
    if (!numbering) return '';
    const nums = refs
      .map((r) => numbering.get(r))
      .filter((n): n is number => typeof n === 'number');
    if (nums.length === 0) return '';
    const min = Math.min(...nums);
    const max = Math.max(...nums);
    return min === max ? `§${min}` : `§${min}–${max}`;
  };

  // § prefixes for the dedicated parties / property panels (per
  // ADR-0049): derived from the distinct section refs their autofill
  // fields belong to, so "Avtalsparter" reads "§1–2" and "Fastighet"
  // reads "§3" without extra declaration. For parties we filter to
  // sections whose fields are ENTIRELY party-autofill — otherwise a
  // guarantor sub-block tucked inside e.g. §10 Säkerhet would inflate
  // the legend to "§1–10".
  const partySectionPrefix = sectionNumberPrefix(
    Array.from(
      new Set(
        allFields
          .filter((f) => f.autofill?.kind === 'party')
          .map((f) => topSectionRef(f.section)),
      ),
    ).filter((sec) =>
      allFields
        .filter((f) => topSectionRef(f.section) === sec)
        .every((f) => f.autofill?.kind === 'party'),
    ),
  );
  // (Property section prefix lives on the embedding panel's legend now —
  // PropertyAutofillGroup is rendered chromeless inside that panel via
  // renderSections, so it no longer needs its own prefix.)

  // Scroll the preview to a section anchor on field focus (ADR-0049).
  // Scoped to the preview's own scroll container ([data-preview-scroll])
  // so it never moves the page — only the pinned preview pane scrolls.
  // No-op for refs with no visible document section (editor-only or
  // currently-hidden sections have no anchor in the rendered preview).
  const scrollPreviewToSection = (ref: string) => {
    if (!numbering || typeof numbering.get(ref) !== 'number') return;
    const el = document.getElementById(sectionAnchorId(ref));
    const container = el?.closest<HTMLElement>('[data-preview-scroll]');
    if (!el || !container) return;
    const delta =
      el.getBoundingClientRect().top - container.getBoundingClientRect().top;
    container.scrollBy({ top: delta - 12, behavior: 'smooth' });
  };

  // For single-property templates we render the property block INSIDE
  // the panel covering that document section (typically §3 Hyresobjekt)
  // instead of as its own standalone panel above. Multi-property
  // templates keep MultiPropertyPanel as a separate top-level panel.
  const propertyOwningSection: string | null =
    !isMultiProperty && propertyFields.length > 0
      ? propertyFields[0]!.section
      : null;

  // Whether the single-property block will embed inside a rendered panel.
  // Panels are built from regular (non-autofill) field groups, so a
  // property section whose only fields are property-autofill (e.g. a
  // one-page form's "Lägenhetens adress") has no panel to embed into. In
  // that case we render the property block as its own top-level panel
  // below the parties — otherwise the section would silently disappear.
  const propertyEmbedded =
    propertyOwningSection !== null &&
    panels.some((p) => p.refs.includes(propertyOwningSection));

  // BH-3: render the guarantor (Borgensman) party-slot card next to its
  // gating control instead of in the top Avtalsparter panel. The slot is
  // gated via `visibleWhen` (e.g. bostadshyra `has_guarantor: true`,
  // lokalhyra `security_type: 'Proprieborgen'`); we anchor the card to
  // the editor panel that owns the gating field's section — 'Borgensman'
  // for bostadshyra, 'sakerhet' (§Säkerhet) for lokalhyra. Generalized
  // (no template-key special-casing): any guarantor slot follows its
  // gating field's section.
  const guarantorSlot =
    tmpl.metadata.partySlots.find(
      (s) =>
        s.role === 'guarantor' &&
        evaluateVisibilityRule(s.visibleWhen, values),
    ) ?? null;
  const guarantorGatingKey: string | null =
    guarantorSlot?.visibleWhen
      ? (Object.keys(guarantorSlot.visibleWhen)[0] ?? null)
      : null;
  const guarantorOwningSection: string | null = guarantorGatingKey
    ? (topSectionRef(
        allFields.find((f) => f.key === guarantorGatingKey)?.section ?? '',
      ) || null)
    : null;
  const guarantorFields = guarantorSlot
    ? allFields.filter(
        (f) =>
          f.autofill?.kind === 'party' &&
          f.autofill.role === guarantorSlot.role &&
          f.autofill.ordinal === guarantorSlot.ordinal,
      )
    : [];
  const guarantorParty = guarantorSlot
    ? (localParties.find(
        (p) =>
          p.role === guarantorSlot.role && p.ordinal === guarantorSlot.ordinal,
      ) ?? null)
    : null;
  // Only exclude from the top panel when we actually have somewhere to
  // embed it — otherwise leave the slot in the Avtalsparter panel.
  const embedGuarantorSlot =
    guarantorSlot !== null && guarantorOwningSection !== null;

  const renderSections = (list: EditorPanel[]) =>
    list.map((p) => {
      const prefix = sectionNumberPrefix(p.refs);
      const embedProperty =
        propertyOwningSection !== null && p.refs.includes(propertyOwningSection);
      const embedGuarantor =
        embedGuarantorSlot &&
        guarantorOwningSection !== null &&
        p.refs.includes(guarantorOwningSection);
      return (
        <fieldset key={p.id} className="flex flex-col gap-3 rounded border border-stone-200 p-3">
          <legend className="flex items-center gap-2 px-1 text-xs font-medium uppercase tracking-wider text-stone-500">
            {prefix && <span className="text-stone-400">{prefix}</span>}
            {p.label}
          </legend>
          {embedProperty && (
            <PropertyAutofillGroup
              token={token}
              fields={propertyFields.filter((f) => isFieldVisible(f, values))}
              values={values}
              linkedId={contract.propertyId}
              defaultGroupId={contract.groupId}
              onLink={linkProperty}
              onUnlink={unlinkProperty}
              onFieldChange={onFieldChange}
              disabled={disabled}
              chromeless
            />
          )}
          {p.fields
            .filter((f) => isFieldVisible(f, values))
            .map((f) => {
              const Renderer = RENDERERS[f.type];
              const isAutoNumber =
                f.key === 'contract_number' && values.__contract_number_auto === true;
              // Conditional lock (disabledWhen): field stays visible but
              // read-only while the rule matches.
              const isRuleDisabled = f.disabledWhen
                ? evaluateVisibilityRule(f.disabledWhen, values)
                : false;
              // If the field targets a sub-clause via a dotted path,
              // resolve the full numeric ref ("22.3.a") and prefix it
              // to the label so editor reads match the document. Falls
              // back silently when there's no path or no declared
              // sub-clauses to resolve against.
              const subPath =
                declaredSections && f.section.includes('.')
                  ? resolveSectionPath(declaredSections, f.section, values, 'final')
                  : null;
              // Conditional-required (requiredWhen): drives the same red
              // asterisk as requiredAtFinal, but only when its rule matches.
              const effectiveRequired =
                !!f.requiredAtFinal ||
                (f.requiredWhen
                  ? evaluateVisibilityRule(f.requiredWhen, values)
                  : false);
              const effectiveLabel =
                subPath !== null ? `${subPath} ${f.label}` : f.label;
              const fieldWithPrefix =
                effectiveLabel !== f.label ||
                effectiveRequired !== !!f.requiredAtFinal
                  ? { ...f, label: effectiveLabel, requiredAtFinal: effectiveRequired }
                  : f;
              // Mutex-group exclusivity: options claimed by a sibling field
              // in the same group are locked here (value → owner label).
              const lockedOptions = f.mutexGroup
                ? (() => {
                    const m = new Map<string, string>();
                    for (const g of allFields) {
                      if (g.key === f.key || g.mutexGroup !== f.mutexGroup) continue;
                      const gv = values[g.key];
                      if (Array.isArray(gv)) {
                        for (const val of gv) {
                          if (typeof val === 'string') m.set(val, g.label);
                        }
                      }
                    }
                    return m;
                  })()
                : undefined;
              return (
                <div
                  key={f.key}
                  data-locate-anchor={locateAnchor('field', f.key)}
                  className="flex flex-col gap-1"
                  onFocusCapture={() => scrollPreviewToSection(topSectionRef(f.section))}
                >
                  <Renderer
                    field={fieldWithPrefix}
                    value={values[f.key]}
                    allValues={values}
                    dataTableXlsx={dataTableXlsxIO}
                    dataTableAutoImport={dataTableAutoImport}
                    onChange={(next) => onFieldChange(f.key, next)}
                    disabled={disabled || isAutoNumber || isRuleDisabled}
                    attachments={
                      contract.attachments as
                        | ReadonlyArray<AttachmentRef>
                        | undefined
                    }
                    onCreateAttachment={
                      f.type === 'attachment_ref' && !disabled
                        ? openCreateAttachment
                        : undefined
                    }
                    lockedOptions={lockedOptions}
                  />
                  {f.durationFrom &&
                    (() => {
                      const total = formatLeaseDuration(
                        values[f.durationFrom],
                        values[f.key],
                      );
                      return total ? (
                        <span className="text-[11px] text-stone-500">
                          Total längd:{' '}
                          <span className="font-medium text-stone-700">{total}</span>
                        </span>
                      ) : null;
                    })()}
                  {f.key === 'contract_number' && (
                    <AutoNumberToggle
                      on={isAutoNumber}
                      pending={reservingNumber}
                      disabled={disabled}
                      onToggle={toggleAutoContractNumber}
                    />
                  )}
                  {(() => {
                    // Inline reserved-slot upload (ADR-0028 Pattern A):
                    // shown beneath the gating field while the slot is
                    // visible for the current values.
                    if (!f.attachmentSlotUpload) return null;
                    const slot = tmpl.metadata.attachmentSlots?.find(
                      (sl) => sl.id === f.attachmentSlotUpload,
                    );
                    if (!slot || !evaluateVisibilityRule(slot.visibleWhen, values)) return null;
                    return (
                      <InlineSlotUpload
                        contractId={contract.id}
                        slotId={slot.id}
                        slotLabel={slot.label}
                        attachments={
                          (contract.attachments as ReadonlyArray<AttachmentRef> | undefined) ?? []
                        }
                        disabled={disabled}
                        onChanged={async () => {
                          await invalidate('contracts.get', contract.id);
                        }}
                      />
                    );
                  })()}
                </div>
              );
            })}
          {embedGuarantor && guarantorSlot && (
            <PartySlotCard
              token={token}
              slot={guarantorSlot}
              party={guarantorParty}
              fields={guarantorFields}
              values={values}
              onAddParty={onAddParty}
              onRemoveParty={onRemoveParty}
              onUpdateParty={onUpdateParty}
              onFieldChange={onFieldChange}
              disabled={disabled}
            />
          )}
        </fieldset>
      );
    });

  const attachmentsCount = contract.attachments?.length ?? 0;

  // The Signering tab is present from draft through signing (see
  // signingTabState); a cancelled contract shows it after "Fortsätt".
  // `effectiveTab` falls back to Avtal if a stale 'signering' selection
  // survives (e.g. archived, or a not-yet-proceeded reload), so the
  // content + strip never desync.
  // ADR-0100: before signing starts (draft, pending, in_review) the tab shows
  // the signing plan, so author and reviewer see the planned signers.
  const signingTab = signingTabState(contract.status, proceeding);
  const signingTabAvailable = signingTab.available;
  const planPhase = signingTab.panel === 'plan';
  // "Kopplade avtal" tab: once the contract leaves draft the inline bar
  // hides its "+ create" affordance, so we surface a dedicated tab to keep
  // creating + managing connected avtal. Only for a root that has children
  // or a connected template it can spawn. (During draft the inline bar
  // handles it, so no tab.)
  const koppladeTabAvailable =
    contract.parentContractId === null &&
    contract.status !== 'draft' &&
    (contract.children.length > 0 || creatableConnected.length > 0);
  const effectiveTab =
    tab === 'signering' && !signingTabAvailable
      ? 'avtal'
      : tab === 'kopplade' && !koppladeTabAvailable
        ? 'avtal'
        : tab;
  const avtalTabShown = !(
    (effectiveTab === 'signering' && signingTabAvailable) ||
    effectiveTab === 'paminnelser' ||
    (effectiveTab === 'kopplade' && koppladeTabAvailable) ||
    (effectiveTab === 'granskning' && hasReviewHistory) ||
    effectiveTab === 'bilagor'
  );
  const proceedToSigning = () => {
    setProceeding(true);
    setTab('signering');
  };

  // REGTA-49 — a missing-entry link in the StatusBar was clicked. Jump to
  // the tab that owns the target (fields/parties live on Avtal, bilaga
  // slots on Bilagor), then let the anchor helper scroll + flash once the
  // target has rendered.
  const locateMissing = (
    kind: LocateKind,
    id: string,
    anchors: ReadonlyArray<string>,
  ) => {
    if (kind === 'attachment_slot') {
      setTab('bilagor');
      setBilagaLocate((cur) => ({ slotId: id, seq: (cur?.seq ?? 0) + 1 }));
    } else {
      setTab('avtal');
      setMobilePreviewOpen(false);
    }
    scrollToLocateAnchor(anchors);
  };

  return (
    <div className="flex h-full flex-col">
      {dataTableAutoImportEl}
      <UnsavedChangesGuard when={dirty && canEdit} />
      <div className={EDITOR_CHROME_WIDTH + ' flex flex-col'}>
      <Link
        to="/contracts"
        className="mb-3 inline-flex w-fit items-center gap-1 text-xs text-stone-500 transition-colors hover:text-stone-800 hover:underline"
      >
        ← Avtal
      </Link>
      <StatusBar
        token={token}
        contract={contract}
        dirty={dirty}
        saveState={saveState}
        onSave={save}
        onPromote={promote}
        onRevertToDraft={revertToDraft}
        onArchive={archive}
        onRequestReview={requestReview}
        onCancelReview={activeReview ? cancelActiveReview : null}
        isActiveReviewer={isActiveReviewer}
        activeReviewerName={
          activeReview
            ? activeReview.reviewer.name ?? activeReview.reviewer.email ?? null
            : null
        }
        lastClosedReviewStatus={
          lastClosedReview
            ? (lastClosedReview.status as 'approved' | 'rejected' | 'cancelled')
            : null
        }
        missing={missing}
        tmpl={tmpl}
        editable={!disabled}
        onProceedToSigning={proceedToSigning}
        onLocateMissing={locateMissing}
      />

      {/*
        Connected-contracts strip — parent breadcrumb when this is a
        child, or children chips + add-popover when this is a parent.
        Rendered above the workflow row so the relationship is always
        in sight, regardless of which tab the user is on.
      */}
      <ConnectedContractsBar
        token={token}
        contractId={contract.id}
        templateKey={contract.templateKey}
        parentContractId={contract.parentContractId}
        parent={contract.parent}
        children={contract.children}
        disabled={disabled}
      />

      {/* An in-flight review stays prominent in the header so the
          reviewer can act without scrolling. Once it's done the outcome +
          history live in the "Granskning" tab. Signing has moved out of
          this row into its own (default) tab — see below. */}
      {actor && activeReview && (
        <div className="mb-4">
          <ReviewPanel token={token} contract={contract} actorId={actor.id} />
        </div>
      )}
      {/* Granskaren has begärt ändringar — surface the rejection
          comment at the top of the page (above the tab strip) so the
          requester sees what to fix without hunting in the Granskning
          tab. Approvals stay archival in the tab. */}
      {!activeReview &&
        lastClosedReview?.status === 'rejected' &&
        (contract.status === 'draft' || contract.status === 'pending') && (
          <div className="mb-4">
            <ClosedReviewBanner review={lastClosedReview} variant="rejected" />
          </div>
        )}
      <div className="mb-3 flex items-center gap-1 border-b border-stone-200">
        {signingTabAvailable && (
          <button
            type="button"
            onClick={() => setTab('signering')}
            className={
              'border-b-2 px-3 py-1.5 text-xs font-medium tracking-wider uppercase transition-colors ' +
              (effectiveTab === 'signering'
                ? 'border-stone-900 text-stone-900'
                : 'border-transparent text-stone-500 hover:text-stone-700')
            }
          >
            Signering
          </button>
        )}
        <button
          type="button"
          onClick={() => setTab('avtal')}
          className={
            'border-b-2 px-3 py-1.5 text-xs font-medium tracking-wider uppercase transition-colors ' +
            (effectiveTab === 'avtal'
              ? 'border-stone-900 text-stone-900'
              : 'border-transparent text-stone-500 hover:text-stone-700')
          }
        >
          Avtal
        </button>
        <button
          type="button"
          onClick={() => setTab('bilagor')}
          className={
            'border-b-2 px-3 py-1.5 text-xs font-medium tracking-wider uppercase transition-colors ' +
            (effectiveTab === 'bilagor'
              ? 'border-stone-900 text-stone-900'
              : 'border-transparent text-stone-500 hover:text-stone-700')
          }
        >
          Bilagor{' '}
          <span className="font-normal text-stone-400">
            ({attachmentsCount})
          </span>
        </button>
        <button
          type="button"
          onClick={() => setTab('paminnelser')}
          className={
            'border-b-2 px-3 py-1.5 text-xs font-medium tracking-wider uppercase transition-colors ' +
            (effectiveTab === 'paminnelser'
              ? 'border-stone-900 text-stone-900'
              : 'border-transparent text-stone-500 hover:text-stone-700')
          }
        >
          Påminnelser{' '}
          <span className="font-normal text-stone-400">({reminderCount})</span>
        </button>
        {hasReviewHistory && (
          <button
            type="button"
            onClick={() => setTab('granskning')}
            className={
              'border-b-2 px-3 py-1.5 text-xs font-medium tracking-wider uppercase transition-colors ' +
              (effectiveTab === 'granskning'
                ? 'border-stone-900 text-stone-900'
                : 'border-transparent text-stone-500 hover:text-stone-700')
            }
          >
            Granskning
          </button>
        )}
        {koppladeTabAvailable && (
          <button
            type="button"
            onClick={() => setTab('kopplade')}
            className={
              'border-b-2 px-3 py-1.5 text-xs font-medium tracking-wider uppercase transition-colors ' +
              (effectiveTab === 'kopplade'
                ? 'border-stone-900 text-stone-900'
                : 'border-transparent text-stone-500 hover:text-stone-700')
            }
          >
            Kopplade avtal{' '}
            <span className="font-normal text-stone-400">
              ({contract.children.length})
            </span>
          </button>
        )}
      </div>
      </div>
      {/* Only the Avtal tab (form + A4 preview) widens on large monitors;
          the header above and the other tabs keep the chrome width. */}
      <div
        className={
          (avtalTabShown ? EDITOR_AVTAL_WIDTH : EDITOR_CHROME_WIDTH) + ' flex flex-1 flex-col'
        }
      >
      {effectiveTab === 'signering' && signingTabAvailable ? (
        <main className="flex flex-1 flex-col overflow-auto bg-white">
          <div className="mx-auto w-full max-w-2xl p-6">
            {planPhase ? (
              <SigningPlanPanel contractId={contract.id} />
            ) : (
              <SigningPanel
                token={token}
                contract={contract}
                signingMode={effectiveSigning}
              />
            )}
          </div>
        </main>
      ) : effectiveTab === 'paminnelser' ? (
        <main className="flex flex-1 flex-col overflow-auto bg-white">
          <NotificationsTab contractId={contract.id} />
        </main>
      ) : effectiveTab === 'kopplade' && koppladeTabAvailable ? (
        <main className="flex flex-1 flex-col overflow-auto bg-white">
          <ConnectedContractsTab
            token={token}
            contractId={contract.id}
            templateKey={contract.templateKey}
            children={contract.children}
          />
        </main>
      ) : effectiveTab === 'granskning' && hasReviewHistory ? (
        <main className="flex flex-1 flex-col overflow-auto bg-white">
          <div className="mx-auto w-full max-w-2xl p-6">
            {actor && (
              <ReviewPanel
                token={token}
                contract={contract}
                actorId={actor.id}
                historyOpen
              />
            )}
          </div>
        </main>
      ) : effectiveTab === 'bilagor' ? (
        <BilagorTab
          token={token}
          contractId={contract.id}
          parentTemplateKey={contract.templateKey}
          parentValues={values}
          attachments={contract.attachments ?? []}
          visibleSlots={(tmpl.metadata.attachmentSlots ?? []).filter((s) =>
            evaluateVisibilityRule(s.visibleWhen, values),
          )}
          missingSlotIds={missing?.attachmentSlots ?? []}
          locateSlot={bilagaLocate}
          onChanged={async () => {
            await invalidate('contracts.get', contract.id);
          }}
          disabled={disabled}
        />
      ) : (
      <>
      {/* Mobile/tablet toggle — flips the avtal tab between the form and
          the live preview. On lg+ both panes sit side-by-side and this
          control is hidden. */}
      <div
        role="tablist"
        aria-label="Visa fält eller förhandsvisning"
        className="mb-3 grid w-full grid-cols-2 overflow-hidden rounded-md border border-stone-300 bg-stone-100 text-sm lg:hidden"
      >
        <button
          type="button"
          role="tab"
          aria-selected={!mobilePreviewOpen}
          onClick={() => setMobilePreviewOpen(false)}
          className={
            'flex items-center justify-center gap-2 px-3 py-2 font-medium transition-colors ' +
            (!mobilePreviewOpen
              ? 'bg-white text-stone-900 shadow-[inset_0_-2px_0_0_var(--color-stone-900)]'
              : 'text-stone-500 hover:bg-stone-200/60 hover:text-stone-800')
          }
        >
          <FileText className="h-4 w-4" />
          Fält
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mobilePreviewOpen}
          onClick={() => setMobilePreviewOpen(true)}
          className={
            'flex items-center justify-center gap-2 border-l border-stone-300 px-3 py-2 font-medium transition-colors ' +
            (mobilePreviewOpen
              ? 'bg-white text-stone-900 shadow-[inset_0_-2px_0_0_var(--color-stone-900)]'
              : 'text-stone-500 hover:bg-stone-200/60 hover:text-stone-800')
          }
        >
          <Eye className="h-4 w-4" />
          Förhandsvisning
        </button>
      </div>
      {/* lg:items-start lets the preview column be shorter than the form
          column so it can stick instead of stretching to the form's height. */}
      <div className="grid flex-1 gap-6 grid-cols-1 lg:grid-cols-[420px_1fr] lg:items-start">
        <aside
          className={
            'min-w-0 flex-col gap-5 lg:flex ' +
            (mobilePreviewOpen ? 'hidden' : 'flex')
          }
        >
          {!disabled && contract.status === 'draft' && (
            <button
              type="button"
              onClick={() => setCopyModalOpen(true)}
              className="group flex items-center gap-3 rounded-md border border-indigo-300 bg-gradient-to-r from-indigo-50 to-white px-3 py-2.5 text-left shadow-sm transition-colors hover:border-indigo-400 hover:from-indigo-100"
            >
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-indigo-600 text-white">
                <Copy className="h-4 w-4" />
              </span>
              <span className="flex flex-col">
                <span className="text-sm font-semibold text-indigo-900">
                  Kopiera från befintligt avtal
                </span>
                <span className="text-[11px] leading-tight text-indigo-700/80">
                  Förifyll motpart, fastighet, bilagor och fält
                </span>
              </span>
            </button>
          )}

          {err && (
            <div className="rounded border border-red-300 bg-red-50 p-3 text-xs text-red-700">
              {err}
            </div>
          )}

          {/* Pre-autofill sections (e.g. Avtalsuppgifter) render above the
              property + parties panels. */}
          {renderSections(preSections)}

          <PartiesPanel
            token={token}
            contractId={contract.id}
            parties={localParties}
            values={values}
            slots={tmpl.metadata.partySlots.filter((s) =>
              evaluateVisibilityRule(s.visibleWhen, values),
            )}
            variableSchema={allFields}
            onAddParty={onAddParty}
            onRemoveParty={onRemoveParty}
            onUpdateParty={onUpdateParty}
            onFieldChange={onFieldChange}
            disabled={disabled}
            sectionPrefix={partySectionPrefix}
            excludeSlot={
              embedGuarantorSlot && guarantorSlot
                ? (s) =>
                    s.role === guarantorSlot.role &&
                    s.ordinal === guarantorSlot.ordinal
                : undefined
            }
          />

          {/* Single-property fallback: when the property's document
              section has no regular-field panel to embed into (an
              autofill-only section, e.g. a one-page form's "Lägenhetens
              adress"), render the property block standalone here so the
              section never disappears from the editor. */}
          {!isMultiProperty && propertyFields.length > 0 && !propertyEmbedded && (
            <PropertyAutofillGroup
              token={token}
              fields={propertyFields.filter((f) => isFieldVisible(f, values))}
              values={values}
              linkedId={contract.propertyId}
              defaultGroupId={contract.groupId}
              onLink={linkProperty}
              onUnlink={unlinkProperty}
              onFieldChange={onFieldChange}
              disabled={disabled}
              sectionPrefix={sectionNumberPrefix(
                propertyOwningSection ? [propertyOwningSection] : [],
              )}
            />
          )}

          {/* Multi-property templates still surface their picker as a
              dedicated top-level panel below the parties. Single-property
              templates have their PropertyAutofillGroup embedded inside
              the §-panel covering the property's document section
              (typically §3 Hyresobjekt) via renderSections. */}
          {isMultiProperty && (
            <MultiPropertyPanel
              token={token}
              contractId={contract.id}
              linkedProperties={contract.linkedProperties}
              defaultGroupId={contract.groupId}
              required={propertySlot?.required}
              label={propertySlot?.label ?? 'Fastigheter'}
              help={propertySlot?.help}
              onChanged={async () => {
                await invalidate('contracts.get', contract.id);
              }}
              disabled={disabled}
            >
              <PropertyListFields
                contractId={contract.id}
                fields={allFields.filter((f) => f.autofill?.kind === 'property_list')}
                values={values}
                disabled={disabled}
                modeLocked={contract.status !== 'draft'}
                onTextChange={(key, next) => onFieldChange(key, next)}
                onModeChanged={(patch) => {
                  setValues((prev) => ({ ...prev, ...patch }));
                  setDirtyKeys((prev) => {
                    const n = new Set(prev);
                    for (const k of Object.keys(patch)) n.delete(k);
                    return n;
                  });
                  void invalidate('contracts.get', contract.id);
                }}
              />
            </MultiPropertyPanel>
          )}

          {renderSections(postSections)}

          {supportsCustomSections && (
            <fieldset className="flex flex-col gap-3 rounded border border-stone-200 p-3">
              <legend className="px-1 text-xs font-medium uppercase tracking-wider text-stone-500">
                Egna sektioner
              </legend>
              <CustomSectionsEditor
                sections={customSections}
                numbering={numbering}
                placementOptions={customPlacementOptions}
                defaultAfterRef={customDefaultAfterRef}
                disabled={disabled}
                onChange={(next) => onFieldChange(CUSTOM_SECTIONS_KEY, next)}
              />
            </fieldset>
          )}

          {disabled && (
            <div className="rounded border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
              {contract.status === 'in_review'
                ? 'Avtalet ligger ute för granskning och kan inte redigeras just nu.'
                : 'Detta avtal är inte i utkast-läge och kan inte redigeras.'}
            </div>
          )}
        </aside>

        {/* On lg+ the preview is pinned under the global header (h-14 =
            3.5rem) and scrolls inside its own bounded height, so the page
            no longer scrolls as one unit while the form column scrolls
            normally. On smaller widths the panes are toggled, so no
            sticky/height constraint. */}
        <main
          className={
            'flex-col overflow-hidden bg-stone-100 lg:flex lg:sticky lg:top-[var(--shell-top,3.5rem)] lg:h-[calc(100vh_-_var(--shell-top,3.5rem))] ' +
            (mobilePreviewOpen ? 'flex' : 'hidden')
          }
        >
          {/*
            Preview-area dispatch (per ADR-0024 §"Contract fixing"):
              - draft / pending / in_review → live React preview
                (the React template is the canonical pre-send view).
              - sent / viewed / awaiting_signature / signed / cancelled
                → frozen PDF inline.
          */}
          <div className="flex min-h-0 flex-1 flex-col">
            {contract.status === 'draft' ||
            contract.status === 'pending' ||
            contract.status === 'in_review' ? (
              <ScaledPreview>
                <DocumentMetaProvider
                  value={{
                    avtalNr: contract.id,
                    objektNr: resolveObjektNr(
                      tmpl.metadata.titleSuffix?.key,
                      values,
                      tmpl.metadata.titleSuffix?.fallbackKey,
                    ),
                  }}
                >
                  <SignIncludePlaceDateContext.Provider
                    value={signingPlan?.preview.includePlaceDate ?? true}
                  >
                  <SignDigitalStatementContext.Provider
                    value={signingPlan?.preview.digitalStatement ?? false}
                  >
                  {(() => {
                    // Multi-document templates (per ADR-0057) preview as
                    // separate framed documents so the split that the
                    // export produces is visible up front. Single-document
                    // templates keep the one-box <Component> path.
                    const docParts = templateDocumentParts(tmpl);
                    const previewAttachments =
                      contract.attachments as ReadonlyArray<AttachmentRef> | undefined;
                    if (docParts.length <= 1) {
                      return (
                        <tmpl.Component
                          values={values}
                          mode="final"
                          attachments={previewAttachments}
                          signatories={signingPlan?.preview.signatories}
                        />
                      );
                    }
                    return docParts.map((part, i) => (
                      <div key={part.key} className={i > 0 ? 'mt-8' : ''}>
                        <div className="mb-2 flex items-center gap-2 text-[11px] font-medium uppercase tracking-wide text-stone-500">
                          <span className="rounded bg-stone-200 px-1.5 py-0.5 text-stone-600">
                            {`Dokument ${i + 1} / ${docParts.length}`}
                          </span>
                          <span className="normal-case tracking-normal text-stone-600">
                            {part.title}
                          </span>
                        </div>
                        <TemplateDocument
                          header={<part.Letterhead />}
                          footer={<part.PageFooter />}
                        >
                          <part.Body
                            values={values}
                            mode="final"
                            attachments={previewAttachments}
                            signatories={signingPlan?.preview.signatories}
                          />
                        </TemplateDocument>
                      </div>
                    ));
                  })()}
                  </SignDigitalStatementContext.Provider>
                  </SignIncludePlaceDateContext.Provider>
                </DocumentMetaProvider>
              </ScaledPreview>
            ) : (
              <FrozenPdfPreview token={token} contractId={contract.id} />
            )}
          </div>
        </main>
      </div>
      </>
      )}
      </div>

      {copyModalOpen && (
        <CopyFromContractModal
          contractId={contract.id}
          onClose={() => setCopyModalOpen(false)}
          onCopied={async () => {
            await Promise.all([
              invalidate('contracts.get', contract.id),
              invalidate('contract_parties.list', contract.id),
            ]);
          }}
        />
      )}
      {createSeed && (
        <CreateBilagaDialog
          contractId={contract.id}
          parentTemplateKey={contract.templateKey}
          defaultName={createSeed.defaultName}
          recommendedTemplate={createSeed.recommendedTemplate}
          onCreated={async (attachmentId) => {
            // Refetch attachments so the new id resolves in the picker
            // before we hand it back for selection.
            await invalidate('contracts.get', contract.id);
            finishCreateAttachment(attachmentId);
          }}
          onClose={() => finishCreateAttachment(null)}
        />
      )}
    </div>
  );
}
